import { Component, inject, Input, OnChanges, OnInit, SimpleChanges, ViewEncapsulation } from '@angular/core'
import { BehaviorSubject, combineLatest, Observable, of } from 'rxjs'
import { map, switchMap, tap } from 'rxjs/operators'
import { EntityReference } from '../../../models/entity-reference'
import { StoredEntityStateService } from '../../../../entity-store-access/services/stored-entity-state.service'
import { EntityService } from '../../../services/entity.service'
import { StoredEntityService } from '@dot-it/jml-lib'

interface EntityInfos {
  type: string
  id: number
  displayHeader: string
  displayDescription: string
}

@Component({
  selector: 'jml-lib-entity-name',
  templateUrl: './entity-name.component.html',
  styleUrls: ['./entity-name.component.scss'],
  encapsulation: ViewEncapsulation.None
})
export class EntityNameComponent implements OnInit, OnChanges {
  @Input() entityType!: string
  @Input() entityId!: number
  @Input() displayHeader!: string
  @Input() displayDescription?: string

  evaluatedDisplayText$: Observable<string>
  evaluatedDisplayDescription$: Observable<string>

  private $entityInfos = new BehaviorSubject<EntityInfos | undefined>(undefined)
  private entityInfos$ = this.$entityInfos.asObservable()

  entityService = inject(EntityService)
  storedEntityService = inject(StoredEntityService)

  ngOnInit(): void {
    this.evaluatedDisplayText$ = this.entityInfos$.pipe(
      switchMap((entityInfos) => this.resolve(entityInfos, entityInfos.displayHeader))
    )
    this.evaluatedDisplayDescription$ = this.entityInfos$.pipe(
      switchMap((entityInfos) => this.resolve(entityInfos, entityInfos.displayDescription))
    )
  }

  private resolve(entityInfos: EntityInfos, displayContent: string): Observable<string> {
    // when there is an expression in the string, we need to check updates of the entity as they might change the displayTxt
    // otherwise we just return the displayTxt
    if (!displayContent || !displayContent.includes('${')) {
      return of(displayContent)
    } else {
      return this.storedEntityService.getEntity$(entityInfos.type, entityInfos.id).pipe(
        map((entity) =>
          displayContent
            .replace('${ENTITY._repr_}', entity.fields['_repr_'])
            .replace('${ENTITY.name}', entity.fields['name'])
        ),
        // now we need to see if still formulas are in displayTxt
        switchMap((displayTxt) => {
          const entityRef = new EntityReference(entityInfos.type, entityInfos.id)
          return this.entityService.eval(displayTxt, null, entityRef).pipe(map((response) => response['result']))
        })
      )
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    const entityInfos = {
      type: !!changes.ownerType?.currentValue ? changes.ownerType.currentValue : this.entityType,
      id: !!changes.ownerId?.currentValue ? changes.ownerId.currentValue : this.entityId,
      displayHeader: !!changes.displayName?.currentValue ? changes.displayName.currentValue : this.displayHeader,
      displayDescription: !!changes.displayDescription?.currentValue
        ? changes.displayDescription.currentValue
        : this.displayDescription
    }
    this.$entityInfos.next(entityInfos)
  }
}
